/**
 * Leviathan - User Form Page
 * Quasar Prime LLC
 *
 * Create/Edit User with Role and Custom Permissions
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import { API_URL } from '@/config/api';
import { isAdminRole, roleOptionsFor } from '@/constants/userRoles';

interface Role {
  id: string;
  name: string;
  permissions: string[];
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  /** UserRole enum — the access level the API enforces. */
  role: string;
  /** Named permission group. Additive: it can only ADD to the role. */
  roleGroup: Role | null;
  customPermissions: string[];
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  /** UserRole enum — the access level the API enforces. */
  role: string;
  /** Named permission group id. Additive: it can only ADD to the role. */
  roleId: string;
  customPermissions: string[];
  isActive: boolean;
}

export default function UserForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'USER',
    roleId: '',
    customPermissions: [],
    isActive: true,
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
    if (isEdit) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/roles/list`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      setRoles(data.roles);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch user');
      const data = await response.json();
      const user: User = data.user;
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        role: user.role,
        roleId: user.roleGroup?.id || '',
        customPermissions: user.customPermissions || [],
        isActive: user.isActive,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const url = isEdit ? `${API_URL}/api/users/${id}` : `${API_URL}/api/users`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          roleId: formData.roleId || null,
        }),
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!isEdit && data.tempPassword) {
        setTempPassword(data.tempPassword);
      } else {
        navigate('/admin/users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const getSelectedRole = (): Role | undefined => {
    return roles.find((r) => r.id === formData.roleId);
  };

  /** "invoices:view" -> "Invoices: view". Groups now store catalog keys. */
  const getPermissionLabel = (permission: string): string => {
    const [module = '', action = ''] = permission.split(':');
    const title = (value: string) =>
      value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return action ? `${title(module)}: ${action}` : title(permission);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show temp password screen after creating user
  if (tempPassword) {
    return (
      <div className="p-6 max-w-lg mx-auto">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">User Created Successfully</h2>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Temporary Password:</p>
            <code className="text-xl font-mono font-bold text-amber-700 block text-center py-2">
              {tempPassword}
            </code>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Please share this password securely with the user. They will be required to change it
              on first login.
            </p>
          </div>

          <button
            onClick={() => navigate('/admin/users')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const selectedRole = getSelectedRole();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/admin/users" className="text-blue-600 hover:underline text-sm">
          &larr; Back to Users
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">
          {isEdit ? 'Edit User' : 'Create User'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {isEdit && (
            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">User is active</span>
              </label>
            </div>
          )}
        </div>

        {/* Access level — the enforced one. This field did not exist on this
            form: it offered only the permission group below, which nothing
            enforces, so an admin could "change someone's role" here and change
            nothing at all about what that person could do. */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Access Level</h3>
          <p className="text-sm text-gray-500 mb-4">
            What this person can do. Enforced on every request.
          </p>

          <select
            value={formData.role}
            onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {roleOptionsFor(formData.role).map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-2">
            {roleOptionsFor(formData.role).find((r) => r.value === formData.role)?.description}
          </p>

          {isAdminRole(formData.role) && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Administrators can manage users, invitations, and company settings — including
              removing other administrators.
            </div>
          )}
        </div>

        {/* Permission group */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Permission Group</h3>
          <p className="text-sm text-gray-500 mb-4">
            A named set of extra permissions, ADDED on top of the access level above. Groups can
            only add — to take something away from one person, use Individual Permissions below,
            so that a removal is never buried in a group shared by other people.
          </p>
          <div>
            <select
              value={formData.roleId}
              onChange={(e) => setFormData((prev) => ({ ...prev, roleId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">No Group</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          {selectedRole && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Role Permissions ({selectedRole.permissions.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedRole.permissions.slice(0, 10).map((perm) => (
                  <span
                    key={perm}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                  >
                    {getPermissionLabel(perm)}
                  </span>
                ))}
                {selectedRole.permissions.length > 10 && (
                  <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                    +{selectedRole.permissions.length - 10} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Per-user permissions used to live here as a grid of checkboxes writing
            to `customPermissions`. That array is no longer what resolution
            reads — it cannot express a REVOKE and it records nothing about who
            granted what — so leaving the grid would recreate the exact bug this
            work removed: a control that looks like it restricts someone and
            does not. The real editor is one screen away and every switch on it
            is enforced by the API. */}
        {isEdit && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Individual Permissions</h3>
            <p className="text-sm text-gray-500 mb-4">
              Grant or revoke specific modules and actions for this person, on top of what their
              access level gives them. Useful when someone wears more than one hat.
            </p>
            <Link
              to={`/admin/users/${id}/permissions`}
              className="inline-block px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Edit permissions for this user &rarr;
            </Link>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link
            to="/admin/users"
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  );
}
